import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Mic, MicOff, PhoneOff } from 'lucide-react';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AiSpeakingOrb } from './AiSpeakingOrb';
import { TranscriptEntry, useLiveAudioSession } from '@/hooks/useLiveAudioSession';
import { trpc } from '@/lib/trpc';
import { createAudioPlayback } from '@/lib/speech-utils';
import { cn } from '@/lib/utils';

type Props = {
  voice?: string;
  initialMessage?: string;
  onProcessMessage: (text: string) => Promise<string>;
  onEndSession?: (transcript: TranscriptEntry[]) => void;
};

const STATE_LABELS: Record<string, string> = {
  idle: 'performance.voice.start',
  recording: 'performance.voice.listening',
  processing: 'performance.voice.processing',
  speaking: 'performance.voice.speaking',
  error: 'performance.voice.error',
};

// Reproducción compartida con las respuestas habladas de D-Talk/Mapa de Talento
async function playBase64Audio(base64: string, mimeType: string): Promise<void> {
  await createAudioPlayback(base64, mimeType).done;
}

export function ConversationalMode({
  voice = 'Lupe',
  initialMessage,
  onProcessMessage,
  onEndSession,
}: Props) {
  const { t } = useTranslation();
  const [isPlayingGreeting, setIsPlayingGreeting] = useState(false);
  const [cachedAudio, setCachedAudio] = useState<{ data: string; mimeType: string } | null>(null);
  const synthesizeSpeech = trpc.performance.realtime.synthesizeSpeech.useMutation();
  const cachingRef = useRef(false);

  // Pre-fetch TTS audio as soon as greeting text is available
  useEffect(() => {
    if (!initialMessage || cachedAudio || cachingRef.current) return;
    cachingRef.current = true;
    synthesizeSpeech.mutateAsync({ text: initialMessage, voice })
      .then((result) => setCachedAudio({ data: result.audio, mimeType: result.mimeType }))
      .catch(() => { cachingRef.current = false; });
  }, [initialMessage, voice]);

  const {
    sessionState,
    transcript,
    isMuted,
    isSpeaking,
    error,
    startSession,
    endSession,
    toggleMute,
  } = useLiveAudioSession({
    voice,
    onProcessMessage,
  });

  const isActive = sessionState === 'recording' || sessionState === 'processing' || sessionState === 'speaking';
  const isListening = sessionState === 'recording' && !isMuted;

  const handleStart = useCallback(async () => {
    if (initialMessage) {
      setIsPlayingGreeting(true);
      try {
        let audio = cachedAudio;
        if (!audio) {
          const r = await synthesizeSpeech.mutateAsync({ text: initialMessage, voice });
          audio = { data: r.audio, mimeType: r.mimeType };
        }
        await playBase64Audio(audio.data, audio.mimeType);
      } catch {
        if (window.speechSynthesis) {
          await new Promise<void>((resolve) => {
            const utt = new SpeechSynthesisUtterance(initialMessage);
            utt.lang = 'es-AR';
            utt.onend = () => resolve();
            utt.onerror = () => resolve();
            window.speechSynthesis.speak(utt);
          });
        }
      } finally {
        setIsPlayingGreeting(false);
      }
    }
    startSession();
  }, [initialMessage, voice, cachedAudio, synthesizeSpeech, startSession]);

  function handleEnd() {
    endSession();
    onEndSession?.(transcript);
  }

  const isPreparingAudio = !!initialMessage && !cachedAudio && !synthesizeSpeech.isError;
  const isLoading = !initialMessage || isPreparingAudio || isPlayingGreeting;

  return (
    <div className="flex flex-col items-center gap-6 py-8 px-4 min-h-[60vh]">
      {/* Avatar with aura */}
      <div className="flex flex-col items-center gap-2">
        <AiSpeakingOrb
          speaking={isSpeaking || isPlayingGreeting}
          listening={isListening}
          size={180}
        />
        <p className="text-sm text-muted-foreground mt-8">
          {isPlayingGreeting
            ? t('performance.voice.speaking')
            : isSpeaking
            ? t('performance.voice.speaking')
            : isListening
            ? t('performance.voice.listening')
            : t(STATE_LABELS[sessionState] ?? 'performance.voice.idle')}
        </p>
        {error && (
          <p className="text-xs text-destructive text-center max-w-xs">
            {error === 'microphone_denied'
              ? t('performance.voice.micDenied')
              : error}
          </p>
        )}
      </div>

      {/* Controls */}
      <div className="flex flex-col items-center gap-3">
        {!isActive && !isPlayingGreeting && (
          <>
            <Button
              size="lg"
              className="rounded-full px-8"
              onClick={handleStart}
              disabled={isLoading}
            >
              {!initialMessage
                ? <><WinclapLoader size={20} className="mr-2" />{t('performance.voice.preparing')}</>
                : isPreparingAudio
                ? <><WinclapLoader size={20} className="mr-2" />{t('performance.voice.loading_voice')}</>
                : <><Mic className="h-5 w-5 mr-2" />{t('performance.voice.start')}</>
              }
            </Button>
            {!initialMessage && (
              <p className="text-xs text-muted-foreground">{t('performance.voice.loading_session')}</p>
            )}
          </>
        )}

        {isPlayingGreeting && (
          <Button size="lg" className="rounded-full px-8" disabled>
            <WinclapLoader size={20} className="mr-2" />
            {t('performance.voice.speaking')}
          </Button>
        )}

        {isActive && (
          <>
            <Button
              variant={isMuted ? 'destructive' : 'outline'}
              size="icon"
              className="h-12 w-12 rounded-full"
              onClick={toggleMute}
              title={isMuted ? t('performance.voice.unmute') : t('performance.voice.mute')}
            >
              {isMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
            </Button>

            <Button
              variant="destructive"
              size="icon"
              className="h-12 w-12 rounded-full"
              onClick={handleEnd}
              title={t('performance.voice.end')}
            >
              <PhoneOff className="h-5 w-5" />
            </Button>
          </>
        )}
      </div>

      {/* Transcript */}
      {transcript.length > 0 && (
        <div className="w-full max-w-xl">
          <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
            {t('performance.voice.transcript')}
          </p>
          <ScrollArea className="h-48 rounded-md border p-3">
            <div className="space-y-3">
              {transcript.map((entry, i) => (
                <div
                  key={i}
                  className={cn(
                    'flex gap-2',
                    entry.role === 'user' ? 'justify-end' : 'justify-start'
                  )}
                >
                  <div
                    className={cn(
                      'rounded-lg px-3 py-1.5 text-sm max-w-[80%]',
                      entry.role === 'user'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted'
                    )}
                  >
                    {entry.text}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      )}
    </div>
  );
}
